/* istanbul ignore file */

import { BrannanNavigation, EventPageFragment } from '@graphql/generated';
import { BoxProps, Flex, TextV2 } from '@withjoy/joykit';
import React, { useEffect, useRef, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { NavLink as RouterLink } from '@react-router';
import { getHoverColor, isSupportedEventPage } from '../../../layout.utils';
import { StyledLegacyNavLinkContainer } from './NavBar.styles';
import EventMenu from '@apps/guest/packages/layout-engine/components/EventMenu/EventMenu';
import { NavbarEventPropsInterface } from '../../../layout.types';
import { formatPagesForBrannan } from './NavBar.utils';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body1ToNavigationOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { pxToRem } from '@withjoy/joykit/theme';

export interface NavBarProps
  extends Readonly<{
      eventHandle: string;
      pages: ReadonlyArray<EventPageFragment>;
      variant: 'inline' | 'menu';
      title?: Maybe<string>;
      isCustomPage?: boolean;
      handleMenuClicked?: (item: string) => void;
      textColor?: string;
      /**
       * When true, the nav will expand to full width.
       * Used in StickyNavBar component
       */
      expandToFullWidth?: boolean;
      /**
       * When true, the nav will not expand to full width even if the page blends seamlessly with the art background.
       * Used in EventNameNavForStandaloneHeader component
       */
      preventExpandingToFullWidth?: boolean;
      /**
       * Optional initial maxWidth value to prevent layout shift during initial render.
       * If not provided, maxWidth starts as undefined and is calculated after mount.
       */
      initialMaxWidth?: number;
    }>,
    Omit<BoxProps, 'title'> {}

const LegacyNavLink: React.FC<{ to: string; isRsvp: boolean; onClick: () => void; colorOverride?: string; isDefaultPage?: boolean }> = ({
  to,
  isRsvp,
  children,
  onClick,
  colorOverride,
  isDefaultPage,
  ...rest
}) => {
  const { pageFontColor } = useColorPaletteProvider();
  const linkColor = useMemo(() => colorOverride || pageFontColor, [colorOverride, pageFontColor]);
  const linkHoverColor = useMemo(() => (linkColor ? getHoverColor(linkColor) : undefined), [linkColor]);

  return (
    <StyledLegacyNavLinkContainer
      _empty={{ display: 'none' }}
      paddingX={5}
      linkColor={linkColor}
      linkHoverColor={linkHoverColor}
    >
      {!isRsvp ? (
        <RouterLink
          onClick={onClick}
          data-testid={(rest as Record<string, string>)['data-testid']}
          to={to}
          exact={isDefaultPage}
        >
          {children}
        </RouterLink>
      ) : (
        <a href={to}>{children}</a>
      )}
    </StyledLegacyNavLinkContainer>
  );
};

const OVERFLOW_BOUND = 860;
const DEFAULT_PADDING = pxToRem(12);
const FULL_WIDTH = '100vw';
const FULL_WIDTH_PADDING = pxToRem(64);

type OverflowState = { maxWidth: number | undefined; hasOverflow: boolean };

const LegacyBrannanOverflowNav: React.FC<Omit<NavBarProps, 'variant' | 'title'>> = ({
  pages,
  eventHandle,
  handleMenuClicked,
  backgroundColor,
  textColor,
  paddingTop = 8,
  borderRadius = 0,
  backdropFilter = 'none',
  border = 'none',
  boxShadow,
  expandToFullWidth,
  preventExpandingToFullWidth,
  initialMaxWidth,
  ...restProps
}) => {
  const containerRef = useRef<HTMLUListElement | null>(null);
  const [state, setState] = useImmer<OverflowState>({
    maxWidth: initialMaxWidth ?? undefined,
    hasOverflow: false,
  });
  const eventPageRoute = useEventPageRoute(eventHandle, pages);
  const { isDefaultPage } = useDefaultPage(pages);
  const { pageColor, artBackgroundColor, brannanInnerFrameBorder, themeId, isCustomArtApplied } = useColorPaletteProvider();

  // Expand nav to full viewport width (FULL_WIDTH) with FULL_WIDTH_PADDING padding when:
  const shouldExpandNav = useMemo(() => {
    if (preventExpandingToFullWidth) {
      return false;
    }

    // When the expand to full width flag is enabled (used in the StickyNavBar component)
    if (expandToFullWidth) {
      return true;
    }

    // When the page blends seamlessly with the art background (matching colors, blank theme, no shadow).
    // Custom theme might have colors and may not blend well.
    return pageColor === artBackgroundColor && themeId === 'blank' && !brannanInnerFrameBorder && !isCustomArtApplied;
  }, [pageColor, artBackgroundColor, themeId, brannanInnerFrameBorder, expandToFullWidth, preventExpandingToFullWidth, isCustomArtApplied]);

  const isPreviewing = isInIframe();

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const _checkOverflow = () => {
        if (container.childElementCount > 0) {
          const results = Array.from(container.children).reduce(
            (acc, child, idx) => {
              if (!acc.hasOverflow) {
                const width = child.getBoundingClientRect().width + 32;
                const hasOverflow = acc.runningWidth + width >= OVERFLOW_BOUND;
                if (!hasOverflow) {
                  acc.runningWidth += width;
                  acc.lastIndex = idx;
                }
                acc.hasOverflow = hasOverflow;
              }

              return acc;
            },
            { runningWidth: 0, lastIndex: 0, hasOverflow: false } as { runningWidth: number; lastIndex: number; hasOverflow: boolean },
          );

          // The last child's offset top will be larger when overflow
          setState(draft => {
            draft.hasOverflow = results.hasOverflow;
            draft.maxWidth = results.hasOverflow ? results.runningWidth : undefined;
          });
        } else {
          setState(draft => {
            draft.hasOverflow = false;
            draft.maxWidth = undefined;
          });
        }
      };

      _checkOverflow();
      const observer = new MutationObserver(() => {
        _checkOverflow();
      });
      observer.observe(container, { childList: true, subtree: true, characterData: true });
    }
  }, [pages, setState]);

  return (
    <Flex
      as="nav"
      {...restProps}
      justifyContent="center"
      paddingTop={paddingTop}
      pointerEvents={isPreviewing ? 'all' : 'unset'}
    >
      <Flex
        as="ul"
        flexWrap={'wrap'}
        maxWidth={shouldExpandNav ? FULL_WIDTH : state.hasOverflow ? '90%' : OVERFLOW_BOUND}
        width={shouldExpandNav ? FULL_WIDTH : undefined}
        minWidth={shouldExpandNav ? FULL_WIDTH : undefined}
        justifyContent="center"
        ref={containerRef}
        backgroundColor={backgroundColor}
        borderRadius={borderRadius}
        border={border}
        backdropFilter={backdropFilter}
        boxShadow={boxShadow}
        paddingX={shouldExpandNav ? FULL_WIDTH_PADDING : DEFAULT_PADDING}
      >
        {pages.map(page => {
          if (!isSupportedEventPage(page.type)) {
            return null;
          }

          const isRsvp = page.pageSlug === 'rsvp';

          return (
            <LegacyNavLink
              onClick={() => handleMenuClicked && handleMenuClicked(page.pageSlug)}
              key={page.id}
              data-testid={`nav-${page.id}`}
              isRsvp={isRsvp}
              to={eventPageRoute.goToPath(page.type, page.pageSlug)}
              colorOverride={textColor}
              isDefaultPage={isDefaultPage(page.pageSlug)}
            >
              <GuestSiteTypographyOverride override={body1ToNavigationOverride}>
                <TextV2
                  typographyVariant="body1"
                  paddingTop={5}
                  paddingBottom={5}
                >
                  {page.pageTitle}
                </TextV2>
              </GuestSiteTypographyOverride>
            </LegacyNavLink>
          );
        })}
      </Flex>
    </Flex>
  );
};

const NavBar: React.FC<NavBarProps & { eventProps: NavbarEventPropsInterface }> = ({
  eventProps,
  eventHandle,
  pages: origPages,
  variant,
  title,
  isCustomPage,
  handleMenuClicked,
  ...restProps
}) => {
  const isVariantMenu = variant === 'menu';
  const isVariantInline = variant === 'inline';
  const [isSimpleScrollLayoutEnabled] = useIsFeatureEnabled('websiteSimpleScrollLayoutEnabled');
  const isSinglePage = isSimpleScrollLayoutEnabled && eventProps.brannanNavigation === BrannanNavigation.singlePage;
  const pages = useMemo(() => (isSinglePage ? origPages : formatPagesForBrannan(origPages)), [origPages, isSinglePage]);
  const { pageColor } = useColorPaletteProvider();

  const eventPropsFormatted = useMemo(
    () => ({
      ...eventProps,
      pages: isSinglePage ? eventProps.pages : formatPagesForBrannan(eventProps.pages),
    }),
    [eventProps, isSinglePage],
  );

  return (
    <>
      {isVariantMenu && (
        <EventMenu
          handleMenuClicked={handleMenuClicked}
          eventHandle={eventHandle}
          title={title}
          eventProps={eventPropsFormatted}
          isCustomPage={isCustomPage}
          backgroundColor={pageColor}
        />
      )}
      {isVariantInline && (
        <LegacyBrannanOverflowNav
          handleMenuClicked={handleMenuClicked}
          pages={pages}
          eventHandle={eventHandle}
          {...restProps}
        />
      )}
    </>
  );
};

NavBar.displayName = 'NavBar';

export { NavBar };
